import React, { useRef, useState, useEffect } from 'react';
import { colors } from '../theme/tokens';

// Mobile-first capture card with three input modes:
//   1. Camera (rear-facing on phones via capture="environment")
//   2. File picker (any image from disk)
//   3. Drag-and-drop onto the silhouette frame (desktop)
// Shows a preview after the user picks an image and offers a "Retake" button.
// Calls onCapture(file) with the chosen File (or null on reset).
const ProfileCaptureCard = ({ onCapture, disabled, busy }) => {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const acceptFile = (f) => {
    if (!f) return;
    if (!/^image\//i.test(f.type)) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setFile(f);
    setPreviewUrl(url);
    onCapture && onCapture(f);
  };

  const handleInputChange = (e) => {
    const f = e.target.files && e.target.files[0];
    acceptFile(f);
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
    onCapture && onCapture(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    acceptFile(f);
  };

  return (
    <div style={styles.card}>
      <div style={styles.headingRow}>
        <span style={styles.heading}>📸 Capture profile cross-section</span>
      </div>

      <ul style={styles.tips}>
        <li>Place a slice flat on white paper</li>
        <li>Fill the frame with the profile</li>
        <li>Use even lighting, avoid hard shadows</li>
      </ul>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {!previewUrl ? (
        <>
          <div
            style={{ ...styles.frameGuide, ...(dragging ? styles.frameGuideDragging : {}) }}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            role="button"
            tabIndex={0}
            aria-label="Drop an image here or click to choose a file"
          >
            <div style={styles.frameCorner1} />
            <div style={styles.frameCorner2} />
            <div style={styles.frameCorner3} />
            <div style={styles.frameCorner4} />
            <div style={styles.frameHint}>
              {dragging
                ? '⬇ Drop image to use it'
                : 'Drop image here, or click to choose a file'}
            </div>
          </div>
          <div style={styles.buttonRow}>
            <button
              type="button"
              disabled={disabled || busy}
              style={{ ...styles.bigBtn, opacity: disabled || busy ? 0.6 : 1 }}
              onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
            >
              📷 Take photo
            </button>
            <button
              type="button"
              disabled={disabled || busy}
              style={{ ...styles.secondaryBigBtn, opacity: disabled || busy ? 0.6 : 1 }}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              📁 Upload image
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={styles.previewWrap}>
            <img src={previewUrl} alt="preview" style={styles.preview} />
            {busy && (
              <div style={styles.busyOverlay}>
                <div style={styles.spinner} />
                <div style={styles.busyLabel}>Analysing…</div>
              </div>
            )}
          </div>
          <div style={styles.actionRow}>
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              style={styles.secondaryBtn}
            >
              ↺ Retake
            </button>
            {file && (
              <div style={styles.fileInfo}>
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
  },
  headingRow: { marginBottom: '8px' },
  heading: { fontSize: '16px', fontWeight: 700, color: colors.text },
  tips: {
    margin: '0 0 12px 18px',
    padding: 0,
    color: colors.textMuted,
    fontSize: '13px',
    lineHeight: 1.5
  },
  frameGuide: {
    position: 'relative',
    aspectRatio: '4 / 3',
    background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.03), rgba(255,255,255,0.03) 12px, rgba(255,255,255,0.06) 12px, rgba(255,255,255,0.06) 24px)',
    border: `2px dashed ${colors.borderStrong}`,
    borderRadius: 10,
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s'
  },
  frameGuideDragging: {
    background: 'repeating-linear-gradient(45deg, rgba(47,182,92,0.12), rgba(47,182,92,0.12) 12px, rgba(47,182,92,0.20) 12px, rgba(47,182,92,0.20) 24px)',
    borderColor: '#2fb65c'
  },
  frameHint: { color: colors.textMuted, fontSize: 13, fontWeight: 600, textAlign: 'center', padding: 12, pointerEvents: 'none' },
  frameCorner1: { position: 'absolute', top: 8, left: 8, width: 18, height: 18, borderTop: '3px solid #2fb65c', borderLeft: '3px solid #2fb65c' },
  frameCorner2: { position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderTop: '3px solid #2fb65c', borderRight: '3px solid #2fb65c' },
  frameCorner3: { position: 'absolute', bottom: 8, left: 8, width: 18, height: 18, borderBottom: '3px solid #2fb65c', borderLeft: '3px solid #2fb65c' },
  frameCorner4: { position: 'absolute', bottom: 8, right: 8, width: 18, height: 18, borderBottom: '3px solid #2fb65c', borderRight: '3px solid #2fb65c' },
  buttonRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  bigBtn: {
    flex: '1 1 200px',
    padding: '16px 18px',
    background: 'linear-gradient(135deg, #2fb65c 0%, #1d8c3a 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(47,182,92,0.3)'
  },
  secondaryBigBtn: {
    flex: '1 1 200px',
    padding: '16px 18px',
    background: colors.surface,
    color: colors.text,
    border: `2px solid ${colors.borderStrong}`,
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  previewWrap: {
    position: 'relative',
    borderRadius: '10px',
    overflow: 'hidden',
    background: colors.surfaceDeep,
    marginBottom: '10px'
  },
  preview: {
    width: '100%',
    maxHeight: '360px',
    objectFit: 'contain',
    display: 'block'
  },
  busyOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(255,255,255,0.25)',
    borderTopColor: '#2fb65c',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  busyLabel: { color: 'white', fontWeight: 600 },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  secondaryBtn: {
    padding: '10px 16px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    color: colors.text
  },
  fileInfo: { fontSize: '12px', color: colors.textMuted }
};

export default ProfileCaptureCard;
